import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "wouter";
import { store, useTables } from "../lib/store";
import { keyOf, spaceBySlug, spacesSorted, viewsOf } from "../lib/select";
import { navigate, openItem, spaceHref } from "../lib/nav";
import { setUi, useUi } from "../lib/keys";
import { setTheme, theme } from "../lib/theme";
import { LAYER, useDismiss } from "./primitives";
import { Icon, VIEW_ICON } from "./icons";

interface Entry {
  id: string;
  group: string;
  label: string;
  /** Muted context after the label, such as the parent item. */
  sub?: string;
  hint?: string;
  icon?: string;
  run: () => void;
}

/** ⌘K: jump to any page or item, or run an action, from the keyboard. */
export function Palette() {
  const { palette } = useUi();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setUi({ palette: !palette });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [palette]);
  return palette ? <PaletteBody /> : null;
}

function PaletteBody() {
  const t = useTables();
  const [loc] = useLocation();
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(0);
  const box = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const close = () => setUi({ palette: false });
  useDismiss(box, close, true, LAYER.palette);
  const currentSpace = loc.startsWith("/s/") ? spaceBySlug(t, loc.split("/")[2]!) : undefined;

  const entries = useMemo(() => {
    const out: Entry[] = [];
    const go = (href: string) => () => (close(), navigate(href));
    out.push({ id: "home", group: "Go to", label: "Home", icon: "home", run: go("/") });
    out.push({ id: "agents", group: "Go to", label: "Agents", icon: "bot", run: go("/agents") });
    out.push({ id: "settings", group: "Go to", label: "Settings and tokens", icon: "key", run: go("/settings") });
    for (const s of spacesSorted(t)) {
      for (const v of viewsOf(t, s.id)) out.push({ id: `v${v.id}`, group: "Go to", label: `${s.name}: ${v.name}`, icon: VIEW_ICON[v.type], run: go(spaceHref(s.slug, v.id)) });
    }
    out.push({ id: "theme", group: "Actions", label: theme() === "day" ? "Switch to the night theme" : "Switch to the day theme", icon: theme() === "day" ? "moon" : "sun", run: () => (setTheme(theme() === "day" ? "night" : "day"), close()) });
    if (currentSpace) {
      out.push({ id: "mine", group: "Actions", label: "Show only my items", hint: "Q", icon: "filter", run: () => (close(), navigate(`${location.pathname}?mine=1`)) });
    }
    const term = q.trim().toLowerCase();
    if (term) {
      const items = [...t.nodes.values()]
        .filter((n) => !n.draft && (n.title.toLowerCase().includes(term) || keyOf(t, n).toLowerCase() === term))
        .sort((a, b) => (a.spaceId === currentSpace?.id ? -1 : 0) - (b.spaceId === currentSpace?.id ? -1 : 0) || b.updatedAt.localeCompare(a.updatedAt))
        .slice(0, 30);
      const found = items.map<Entry>((n) => {
        const parent = n.parentId ? t.nodes.get(n.parentId) : undefined;
        const space = t.spaces.get(n.spaceId);
        return { id: n.id, group: "Items", label: n.title, sub: parent ? parent.title : space?.name, hint: keyOf(t, n), icon: t.kinds.get(n.kindId)?.icon ?? "dot", run: () => (close(), openItem(keyOf(store.t, n))) };
      });
      const rest = out.filter((e) => e.label.toLowerCase().includes(term));
      return [...found, ...rest];
    }
    return out;
  }, [t, q, currentSpace]);

  useEffect(() => setSel(0), [q]);
  useEffect(() => {
    list.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: "nearest" });
  }, [sel]);

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") (e.preventDefault(), setSel((s) => Math.min(s + 1, entries.length - 1)));
    else if (e.key === "ArrowUp") (e.preventDefault(), setSel((s) => Math.max(s - 1, 0)));
    else if (e.key === "Enter" && entries[sel]) (e.preventDefault(), entries[sel]!.run());
  };

  let group = "";
  return createPortal(
    <>
      <div className="scrim" style={{ zIndex: 65 }} />
      <div ref={box} className="palette" data-layer={LAYER.palette} role="dialog" aria-modal="true" aria-label="Search and commands">
        <input autoFocus data-escape-closes value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey} placeholder="Search items, or jump to a page" aria-label="Search" aria-controls="palette-list" aria-activedescendant={entries[sel] ? `pe-${entries[sel]!.id}` : undefined} />
        <div ref={list} id="palette-list" className="palette-list" role="listbox">
          {entries.map((e, i) => {
            const head = e.group !== group ? ((group = e.group), <div key={`g${e.group}`} className="pg">{e.group}</div>) : null;
            return (
              <div key={e.id}>
                {head}
                <button id={`pe-${e.id}`} type="button" className="pi" role="option" aria-selected={i === sel} onMouseMove={() => setSel(i)} onClick={e.run}>
                  <Icon name={e.icon ?? "dot"} />
                  <span className="grow" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {e.label}
                    {e.sub && <span className="muted"> · {e.sub}</span>}
                  </span>
                  {e.hint && <span className="pk">{e.hint}</span>}
                </button>
              </div>
            );
          })}
          {!entries.length && <div className="pi muted">No matches for "{q}"</div>}
        </div>
        <div className="palette-foot">
          <span>
            <span className="kbd">↑</span> <span className="kbd">↓</span> to move
          </span>
          <span>
            <span className="kbd">Enter</span> to open
          </span>
          <span>
            <span className="kbd">Esc</span> to close
          </span>
        </div>
      </div>
    </>,
    document.body,
  );
}
